import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Live2DStage } from '../../components/live2d';
import { snappyTween, staggerEntrance } from './motionUi';
import {
  albumsForShop,
  albumReward,
  characterAlbums,
  characterItems,
  characterProfile,
  gifts,
  relationshipUp,
  tokenPacks
} from '../fixtures/uiKitFixtures';
import GameTokenPurchasePanel from './GameTokenPurchasePanel';

const shopCardSpring = { type: 'spring', damping: 19, stiffness: 340 };

export function GameAlbumPurchaseModal() {
  return (
    <div className="space-y-3 p-4">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {albumsForShop.map((a, i) => (
          <motion.button
            key={a.id}
            type="button"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={staggerEntrance(i, 0.05, 0)}
            className="flex flex-col overflow-hidden rounded-xl border border-white/10 bg-game-surface text-left shadow-lg transition-transform duration-75 ease-out hover:-translate-y-0.5"
          >
            <div className="aspect-square w-full shrink-0 bg-gradient-to-br from-game-popup via-game-purpleMid to-game-banner" />
            <div className="p-2">
              <p className="line-clamp-2 text-[11px] font-semibold leading-snug text-game-text">{a.title}</p>
              <p className="mt-0.5 text-[10px] text-game-muted">{a.photoCount} photos</p>
              <p className="mt-1 text-[11px] font-bold text-game-accent">{a.price} tokens</p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export function GameTokenPurchaseModal() {
  const packs = tokenPacks.map((p) => ({
    _id: p.id,
    name: p.label,
    tokens: p.amount,
    image: '',
    priceDisplay: p.price
  }));
  return <GameTokenPurchasePanel packs={packs} />;
}

const gallerySideNavClass =
  'absolute top-1/2 z-30 flex h-12 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-game-canvas/95 text-xl font-bold leading-none text-game-text shadow-md backdrop-blur-sm transition hover:border-game-accent/45 hover:bg-game-accent/10 sm:h-14 sm:w-11 sm:text-2xl disabled:cursor-not-allowed disabled:opacity-35';

/**
 * @param {object} [props]
 * @param {Array<{ name: string, description?: string, relationshipLabel?: string, rpCurrent?: number, rpNext?: number, preferences?: string, _id?: string, sessionNumber?: number, live2dModelUrl?: string, live2dIdleMotionIndex?: number, avatarGridUrl?: string }>} [props.profiles]
 * @param {number} [props.characterIndex]
 * @param {(i: number) => void} [props.onCharacterIndexChange]
 * @param {(profile: object) => void} [props.onPhotosClick]
 * @param {(profile: object) => void} [props.onPlayClick]
 * @param {(profile: object) => void} [props.onChatClick]
 */
export function GameCharacterWindow({
  profiles,
  characterIndex: controlledIndex,
  onCharacterIndexChange,
  onPhotosClick,
  onPlayClick,
  onChatClick
} = {}) {
  const list = profiles && profiles.length > 0 ? profiles : [characterProfile];
  const [localIdx, setLocalIdx] = useState(0);
  const controlled =
    typeof controlledIndex === 'number' && typeof onCharacterIndexChange === 'function';
  const idx = controlled
    ? Math.max(0, Math.min(controlledIndex, list.length - 1))
    : Math.max(0, Math.min(localIdx, list.length - 1));
  const profile = list[idx];
  const showArrows = list.length > 1;

  useEffect(() => {
    if (!controlled && localIdx >= list.length) {
      setLocalIdx(0);
    }
  }, [controlled, list.length, localIdx]);

  const setIndex = (next) => {
    const n = list.length;
    const i = ((next % n) + n) % n;
    if (controlled) onCharacterIndexChange(i);
    else setLocalIdx(i);
  };

  const pct = Math.min(
    100,
    Math.round(((Number(profile.rpCurrent) || 0) / Math.max(1, Number(profile.rpNext) || 1)) * 100)
  );

  return (
    <div className="relative flex h-full min-h-0 w-full flex-1 flex-col overflow-visible">
      {/*
       * Side nav (‹ ›) is `absolute` on this full-bleed wrapper. Inner content MUST keep
       * generous horizontal padding (see grid below) so arrows never sit on top of Live2D/text.
       */}
      {showArrows ? (
        <>
          <button
            type="button"
            className={`${gallerySideNavClass} left-2 sm:left-3 md:left-4`}
            aria-label="Previous character"
            onClick={() => setIndex(idx - 1)}
          >
            ‹
          </button>
          <button
            type="button"
            className={`${gallerySideNavClass} right-2 sm:right-3 md:right-4`}
            aria-label="Next character"
            onClick={() => setIndex(idx + 1)}
          >
            ›
          </button>
        </>
      ) : null}
      {/* ---------------------------------------------------------------------------
       * GameCharacterWindow — LAYOUT CONTRACT (read before changing):
       * Two columns ~50/50: Live2D (Pixi canvas alpha 0 → shows lobby bg-game-page) | details.
       * Right column may overlap Live2D slightly. Do NOT stack to one column.
       * HORIZONTAL INSET: px-12+ required — prev/next arrows are absolute on the parent; tiny
       * px-3/5 makes arrows cover the portrait.
       * Live2D: full-bleed inset-0; anchor X = left column (0.25). Vertical nudge = layoutBottomShiftRatio (moves model
       * in Pixi, not CSS — avoids clipping the face at the top of the canvas). Gradient via bg-game-page under transparent canvas.
       * --------------------------------------------------------------------------- */}
      <div className="relative grid h-full min-h-0 w-full flex-1 grid-cols-2 gap-0 overflow-visible px-12 pb-0 pt-3 min-[480px]:px-16 min-[480px]:pt-4 md:px-20 lg:px-24 [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
        {profile.live2dModelUrl ? (
          <div className="pointer-events-none absolute inset-0 z-0 overflow-visible">
            <div className="absolute inset-0 z-0 bg-game-page" aria-hidden />
            <div className="relative z-[1] flex h-full min-h-0 w-full min-w-0 flex-col">
              <Live2DStage
                key={`${profile.live2dModelUrl}-${profile.live2dIdleMotionIndex ?? 0}`}
                layoutVerticalAlign="bottom"
                layoutScaleMultiplier={1.5}
                layoutHorizontalRatio={0.25}
                layoutBottomShiftRatio={0.35}
                backgroundColor={0}
                backgroundAlpha={0}
                className="h-full min-h-0 min-w-0 w-full flex-1"
                modelUrl={profile.live2dModelUrl}
                motionGroup="idle"
                motionIndex={Number(profile.live2dIdleMotionIndex) || 0}
                speedMultiplier={1}
                showLoadingOverlay={false}
              />
            </div>
          </div>
        ) : null}
        <div
          className={`relative z-[1] flex min-h-0 min-w-0 flex-col ${
            profile.live2dModelUrl ? 'pointer-events-none' : ''
          }`}
        >
          <motion.div
            key={`${profile._id || profile.name}-portrait`}
            className={`relative flex h-full min-h-0 w-full min-w-0 flex-col ${
              profile.live2dModelUrl
                ? 'overflow-visible bg-transparent'
                : 'overflow-hidden rounded-2xl border border-white/10 bg-game-surface/60'
            }`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', damping: 20, stiffness: 280 }}
          >
            {profile.live2dModelUrl ? (
              <div className="h-full min-h-0 w-full shrink-0" aria-hidden />
            ) : (
              <div className="flex h-full min-h-0 flex-col items-center justify-center gap-2 px-2 py-6 text-center">
                <span className="text-4xl font-semibold text-game-muted/35" aria-hidden>
                  {profile.name?.[0]?.toUpperCase() || '?'}
                </span>
                <p className="max-w-[14rem] text-sm leading-relaxed text-game-muted">
                  No Live2D for this character. Assign pack, model, and idle motion in Admin.
                </p>
              </div>
            )}
          </motion.div>
        </div>
        <div className="relative z-10 -ml-[max(10%,2.5rem)] flex min-h-0 min-w-0 flex-col justify-center space-y-4 self-stretch overflow-hidden rounded-l-2xl bg-gradient-to-l from-game-canvas/88 from-25% via-game-canvas/55 via-55% to-transparent to-100% py-1 pl-5 pr-1 min-[480px]:-ml-[12%] min-[480px]:pl-7">
          <div className="[text-shadow:0_1px_14px_rgba(0,0,0,0.5)]">
            <h3 className="text-xl font-bold text-game-text sm:text-2xl">{profile.name}</h3>
            <p className="mt-1.5 text-base leading-relaxed text-game-body sm:text-[1.05rem]">
              {profile.description}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-game-muted">Relationship</p>
            <p className="text-base font-semibold text-game-accent sm:text-lg">{profile.relationshipLabel}</p>
          </div>
          <div>
            <div className="mb-1.5 flex justify-between text-xs text-game-muted sm:text-sm">
              <span>RP to next level</span>
              <span className="tabular-nums text-game-text">
                {profile.rpCurrent} / {profile.rpNext}
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-game-surface sm:h-3">
              <motion.div
                key={`${profile.name}-bar`}
                className="h-full rounded-full bg-gradient-to-r from-game-popup to-game-accent"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ type: 'spring', damping: 20, stiffness: 120 }}
              />
            </div>
          </div>
          <div className="[text-shadow:0_1px_12px_rgba(0,0,0,0.45)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-game-muted">Role</p>
            <p className="text-base text-game-body sm:text-[1.05rem]">{profile.preferences}</p>
          </div>
          <div className="flex flex-wrap gap-2.5 pt-1 [text-shadow:0_1px_10px_rgba(0,0,0,0.4)]">
            {[
              { label: 'Photos', action: 'albums', handler: () => onPhotosClick?.(profile) },
              { label: 'Chat', action: 'chat', handler: () => onChatClick?.(profile) },
              { label: 'Play', action: 'play', handler: () => onPlayClick?.(profile) }
            ].map(({ label, action, handler }, i) => (
              <motion.button
                key={action}
                type="button"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={staggerEntrance(i, 0.05, 0.15)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold sm:px-6 sm:py-3 sm:text-base ${
                  action === 'play'
                    ? 'bg-game-accent text-game-accentText'
                    : 'border border-white/15 bg-game-surface text-game-text'
                }`}
                onClick={handler}
              >
                {label}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} [props]
 * @param {{ fromLabel: string; toLabel: string; rewardAlbum?: { title?: string; photoCount?: number; setId?: string; coverImageUrl?: string } | null }} [props.payload] — omit for UI kit demo (fixture)
 * @param {() => void} [props.onOpenAlbum] — opens reward set when `rewardAlbum.setId` is set
 */
export function GameRelationshipUpModal({ payload, onOpenAlbum } = {}) {
  const demo = payload == null;
  const fromLabel = demo ? relationshipUp.from : payload.fromLabel;
  const toLabel = demo ? relationshipUp.to : payload.toLabel;
  const reward = demo
    ? relationshipUp.rewardAlbum?.optional
      ? relationshipUp.rewardAlbum
      : null
    : payload.rewardAlbum ?? null;
  const showReward = Boolean(reward && (reward.title || reward.photoCount));
  const canOpenAlbum = Boolean(
    showReward && reward.setId && typeof onOpenAlbum === 'function'
  );
  return (
    <div className="space-y-4 p-6 text-center">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-game-accent/25 text-3xl text-game-accent"
      >
        ✦
      </motion.div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-game-muted">Relationship up</p>
      <div className="flex items-center justify-center gap-3 text-sm">
        <span className="rounded-lg bg-game-surface px-3 py-1.5 text-game-muted line-through decoration-game-muted">
          {fromLabel}
        </span>
        <span className="text-game-accent">→</span>
        <motion.span
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          className="rounded-lg bg-game-accent/20 px-3 py-1.5 font-bold text-game-text"
        >
          {toLabel}
        </motion.span>
      </div>
      {showReward ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ opacity: { delay: 0.2, duration: 0.2 }, y: { delay: 0.2, duration: 0.2 } }}
          className="flex items-center gap-3 rounded-xl border border-game-accent/30 bg-game-surface/80 p-3 text-left"
        >
          <div className="aspect-square h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-game-popup to-game-purpleDeep">
            {reward.coverImageUrl ? (
              <img
                src={reward.coverImageUrl}
                alt=""
                className="h-full w-full object-cover"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-game-muted">Reward</p>
            <p className="mt-0.5 text-sm font-semibold text-game-accent">{reward.title}</p>
            {reward.photoCount ? (
              <p className="mt-0.5 text-[10px] text-game-muted">
                {reward.photoCount} {reward.photoCount === 1 ? 'item' : 'items'}
              </p>
            ) : null}
          </div>
          {canOpenAlbum ? (
            <motion.button
              type="button"
              transition={snappyTween}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="shrink-0 self-center rounded-xl bg-game-accent px-3 py-2 text-xs font-bold text-game-accentText"
              onClick={onOpenAlbum}
            >
              Open
            </motion.button>
          ) : null}
        </motion.div>
      ) : null}
    </div>
  );
}

/**
 * @param {{ payload?: { title?: string; photoCount?: number; setId?: string; coverImageUrl?: string } | null; onOpen?: () => void }} [props]
 * — без props: демо из UI kit (fixtures).
 */
export function GameAlbumRewardModal({ payload = null, onOpen } = {}) {
  const demo = payload == null;
  const subtitle = demo ? albumReward.subtitle : 'Album unlocked';
  const title = demo ? albumReward.title : String(payload?.title || 'Album').trim() || 'Album';
  const photoCount = demo ? albumReward.photoCount : Number(payload?.photoCount) || 0;
  const coverUrl = demo ? '' : String(payload?.coverImageUrl || '').trim();
  const canOpen = Boolean(!demo && payload?.setId && typeof onOpen === 'function');

  return (
    <div className="space-y-4 p-6 text-center">
      <motion.div
        className="mx-auto aspect-square w-[min(11rem,72vw)] max-w-full overflow-hidden rounded-xl border-2 border-game-accent/50 shadow-xl"
        initial={{ rotate: -4, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 14, stiffness: 200 }}
      >
        <div className="relative h-full w-full bg-gradient-to-br from-game-popup via-game-purpleMid to-game-purpleDeep">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : null}
        </div>
      </motion.div>
      <div>
        <p className="text-xs uppercase tracking-wide text-game-muted">{subtitle}</p>
        <p className="mt-1 text-lg font-bold text-game-text">{title}</p>
        {photoCount > 0 ? (
          <p className="mt-1 text-[11px] text-game-muted">
            {photoCount} {photoCount === 1 ? 'photo' : 'photos'}
          </p>
        ) : null}
      </div>
      <motion.button
        type="button"
        transition={snappyTween}
        whileHover={{ scale: canOpen || demo ? 1.02 : 1 }}
        whileTap={{ scale: canOpen || demo ? 0.98 : 1 }}
        disabled={!demo && !canOpen}
        className="w-full rounded-xl bg-game-accent py-3 text-sm font-bold text-game-accentText disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => {
          if (canOpen) onOpen();
        }}
      >
        Open
      </motion.button>
    </div>
  );
}

const coverGradients = [
  'from-game-popup via-game-purpleMid to-game-banner',
  'from-game-menuTop via-game-popup to-game-purpleDeep',
  'from-game-purpleMid to-game-banner',
  'from-game-popup to-game-menuTop'
];

export function GameCharacterAlbumsModal() {
  return (
    <div className="p-3 sm:p-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {characterAlbums.map((a, i) => (
          <motion.button
            key={a.id}
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={staggerEntrance(i, 0.05, 0)}
            whileTap={{ scale: 0.99 }}
            className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-game-surface text-left shadow-lg transition-transform duration-75 ease-out hover:-translate-y-0.5"
          >
            <div className="relative aspect-square w-full shrink-0 overflow-hidden">
              <div
                className={`h-full w-full bg-gradient-to-br ${coverGradients[i % coverGradients.length]}`}
              />
              {a.owned ? (
                <span className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                  Library
                </span>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 px-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">Locked</span>
                  <span className="text-sm font-bold tabular-nums text-game-accent">{a.price} ◆</span>
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col p-2.5 sm:p-3">
              <p className="line-clamp-2 text-xs font-semibold leading-snug text-game-text sm:text-[13px]">{a.title}</p>
              <p className="mt-1 text-[10px] text-game-muted sm:text-[11px]">{a.photoCount} photos</p>
              <div
                className={`mt-3 w-full rounded-xl py-2.5 text-center text-[11px] font-bold sm:text-xs ${
                  a.owned
                    ? 'border border-white/15 bg-transparent text-game-text'
                    : 'bg-game-accent text-game-accentText'
                }`}
              >
                {a.owned ? 'Open' : 'Unlock'}
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export function GameGiftsModal() {
  return (
    <ul className="divide-y divide-white/10 p-2">
      {gifts.map((g, i) => (
        <motion.li
          key={g.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ opacity: { delay: i * 0.05, duration: 0.18, ease: 'easeOut' } }}
          className="flex items-center gap-3 py-3"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-game-surface text-xl">
            {g.emoji}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-game-text">{g.name}</p>
            <p className="text-xs text-game-accent">{g.price} tokens</p>
          </div>
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="rounded-lg bg-game-accent px-3 py-1.5 text-xs font-bold text-game-accentText"
          >
            Send
          </motion.button>
        </motion.li>
      ))}
    </ul>
  );
}

export function GameItemShopModal({ characterName }) {
  return (
    <div className="p-3 sm:p-4">
      <motion.p
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="mb-3 border-b border-white/10 pb-3 text-center text-sm font-semibold tracking-tight text-game-text"
      >
        {characterName}
      </motion.p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
        {characterItems.map((it, i) => (
          <motion.div
            key={it.id}
            layout
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ ...shopCardSpring, delay: i * 0.07 }}
            whileHover={{
              y: -4,
              transition: { type: 'spring', stiffness: 400, damping: 22 }
            }}
            className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-game-surface/90 to-game-surface/50 p-4 shadow-md transition-colors duration-150 hover:border-game-accent/35 hover:shadow-lg hover:shadow-game-accent/[0.07]"
          >
            <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-game-accent/40 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
            <motion.div
              className="mb-3 flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-2xl border border-white/10 bg-game-canvas/90 text-[2rem] shadow-inner"
              whileHover={{
                scale: 1.08,
                rotate: [0, -5, 5, 0],
                transition: { duration: 0.4, ease: 'easeInOut' }
              }}
            >
              {it.emoji}
            </motion.div>
            <p className="text-center text-sm font-semibold text-game-text">{it.name}</p>
            <p className="mt-1 text-xs font-bold tabular-nums text-game-accent">{it.price} ◆</p>
            <motion.button
              type="button"
              transition={snappyTween}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="mt-4 w-full rounded-xl border border-game-accent/55 bg-game-accent/10 py-2.5 text-xs font-bold text-game-accent transition-colors hover:bg-game-accent/20"
            >
              Buy
            </motion.button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
